import { ng } from "@common/ngds";

import { geom_style_concrete, geom_style_steel, geom_style_timber } from "../ViewportStyles";

export const findSectionInProject = (projectData: ng.frontend.column.Project | null) => {
  const first_element = projectData?.data?.input?.structures?.[0]?.elements[0];
  const first_segment = first_element?.getBeam()?.segments?.[0];
  const secGuid = first_segment?.layer?.start?.secGuid;
  const allSecs = projectData?.data?.input?.secDb.filter((x) => x.id?.guid === secGuid);
  const sec = allSecs && allSecs.length == 1 ? allSecs[0] : null;
  const hasData = secGuid !== undefined;
  return { allSecs, sec, first_element, first_segment, secGuid, hasData };
};

// "<section-name> / <material-name>" label, or null when no name is known.
// the section name only carries information for steel profiles (HEA200, ...),
// concrete and timber sections only show the material name
export const section_material_label = (projectData: ng.frontend.column.Project | null) => {
  const { sec, first_element } = findSectionInProject(projectData);
  const mtrlGuid = first_element?.getBeam()?.mtrlGuid;
  const mtrl = projectData?.data?.input?.mtrlDb?.find((x) => x.id?.guid === mtrlGuid);
  const is_steel = sec?.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_STEEL;
  const names = [is_steel ? sec?.id?.name : undefined, mtrl?.id?.name].filter(
    (x): x is string => x !== undefined && x.length > 0,
  );
  if (names.length === 0) {
    return null;
  }
  return names.join(" / ");
};

export const geomStyle_from_materialCategory = (cat: ng.sections.MaterialCategory | undefined) => {
  switch (cat) {
    case ng.sections.MaterialCategory.MATERIAL_CATEGORY_CONCRETE:
      return geom_style_concrete;
    case ng.sections.MaterialCategory.MATERIAL_CATEGORY_TIMBER:
      return geom_style_timber;
    case ng.sections.MaterialCategory.MATERIAL_CATEGORY_STEEL:
      return geom_style_steel;
    default:
      return geom_style_concrete;
  }
};

export const columnMaterialFill = (projectData: ng.frontend.column.Project | null): string =>
  geomStyle_from_materialCategory(findSectionInProject(projectData).sec?.materialCategory).fill ?? "transparent";

export const columnLighterPercent = (
  projectData: ng.frontend.column.Project | null,
  timberPercent: number,
  otherMaterialsPercent: number,
): number =>
  findSectionInProject(projectData).sec?.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_TIMBER
    ? timberPercent
    : otherMaterialsPercent;

// The column takes its material's 2D fill; the top face and the lit end of the shading mix it
// towards white, the unlit end towards black, by the 3D view settings' lighter/darker percentages.
export const columnTopFill = (fill: string, lighterPercent: number): string =>
  `color-mix(in oklab, ${fill} ${100 - lighterPercent}%, white)`;

const columnBottomFill = (fill: string, darkerPercent: number): string =>
  `color-mix(in oklab, ${fill} ${100 - darkerPercent}%, black)`;

export const columnShadedFill = (
  fill: string,
  lightness: number,
  lighterPercent: number,
  darkerPercent: number,
): string =>
  `color-mix(in oklab, ${columnBottomFill(fill, darkerPercent)} ${(1 - lightness) * 100}%, ${columnTopFill(fill, lighterPercent)})`;

// convert a number [meter] to "default" display text [meter]
// Example: 4 (for 4.0) and 24.5 (for 24.51)
export const display_default_meassure_from_meter = (meter: number): string => {
  // todo(Gustav): use user units
  const withDecimalPoint = meter.toFixed(1); // string value is 4.0 or 24.5
  const clippedNumber = Number.parseFloat(withDecimalPoint); // number value is 4.0 or 24.5
  const removedPointZero = clippedNumber.toString(); // now a string that removes .0 from the 4 but not .5 from 24.5
  return removedPointZero;
};

// convert a number [meter] to "detailed" display text [milli-meter]
export const dsiplay_detail_meassure_from_meter = (m: number) => {
  return (m * 1000).toFixed(0);
};
